import React, { useState, useEffect } from 'react'
import { Deck } from '../types'
import { apiClient } from '../api/client'
import { useAuth } from '../contexts/AuthContext'
import './DeckManagement.css'

interface DeckManagementProps {
  onSelectDeck?: (deckId: string) => void
}

// Field length limits from the deck-management spec.
const NAME_MAX = 100
const DESCRIPTION_MAX = 500
const CATEGORY_MAX = 50

function mapErrorToJapanese(err: any): string {
  const status = err?.response?.status
  if (status === 401) return 'ログインが必要です。再度サインインしてください。'
  if (status === 403) return 'この操作を行う権限がありません。'
  if (status === 404) return 'デッキが見つかりませんでした。'
  if (status === 400 || status === 422) return '入力内容に誤りがあります。確認してください。'
  return '通信エラーが発生しました。時間をおいてもう一度お試しください。'
}

const DeckManagement: React.FC<DeckManagementProps> = ({ onSelectDeck }) => {
  const { user } = useAuth()
  const [decks, setDecks] = useState<Deck[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // Add-deck form state
  const [showAddForm, setShowAddForm] = useState(false)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState('')
  const [validationError, setValidationError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  // Delete-deck state
  const [deckToDelete, setDeckToDelete] = useState<Deck | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  // Rename-deck state
  const [deckToRename, setDeckToRename] = useState<Deck | null>(null)
  const [renameName, setRenameName] = useState('')
  const [renaming, setRenaming] = useState(false)
  const [renameError, setRenameError] = useState<string | null>(null)

  useEffect(() => {
    loadDecks()
  }, [])

  const loadDecks = async () => {
    setLoading(true)
    setError(null)
    try {
      if (!user?.user_id) {
        setDecks([])
        return
      }
      const res = await apiClient.getDecks()
      setDecks(res.data ?? [])
    } catch (err: any) {
      setError(mapErrorToJapanese(err))
    } finally {
      setLoading(false)
    }
  }

  const validateName = (value: string): string | null => {
    const trimmed = value.trim()
    if (trimmed.length === 0) return 'デッキ名を入力してください'
    if (trimmed.length > NAME_MAX) return 'デッキ名は100文字以内で入力してください'
    return null
  }

  const resetForm = () => {
    setName('')
    setDescription('')
    setCategory('')
    setValidationError(null)
    setShowAddForm(false)
  }

  const handleAddSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setValidationError(null)

    const nameError = validateName(name)
    if (nameError) {
      setValidationError(nameError)
      return
    }
    const descTrim = description.trim()
    if (descTrim.length > DESCRIPTION_MAX) {
      setValidationError('説明は500文字以内で入力してください')
      return
    }
    const catTrim = category.trim()
    if (catTrim.length > CATEGORY_MAX) {
      setValidationError('カテゴリは50文字以内で入力してください')
      return
    }

    setCreating(true)
    try {
      const res = await apiClient.createDeck({
        name: name.trim(),
        description: descTrim || undefined,
        category: catTrim || undefined,
      })
      const created = res.data
      if (created) {
        setDecks(prev => [created, ...prev])
        resetForm()
      } else {
        setValidationError(mapErrorToJapanese({ response: { status: 500 } }))
      }
    } catch (err: any) {
      setValidationError(mapErrorToJapanese(err))
    } finally {
      setCreating(false)
    }
  }

  const confirmDelete = async () => {
    if (!deckToDelete) return
    setDeleting(true)
    setDeleteError(null)
    try {
      await apiClient.deleteDeck(deckToDelete.deck_id)
      setDecks(prev => prev.filter(d => d.deck_id !== deckToDelete.deck_id))
      setDeckToDelete(null)
    } catch (err: any) {
      setDeleteError(mapErrorToJapanese(err))
    } finally {
      setDeleting(false)
    }
  }

  const handleRenameSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!deckToRename) return
    setRenameError(null)

    const nameError = validateName(renameName)
    if (nameError) {
      setRenameError(nameError)
      return
    }

    setRenaming(true)
    try {
      const res = await apiClient.updateDeck(deckToRename.deck_id, {
        name: renameName.trim(),
      })
      const updated = res.data
      if (updated) {
        setDecks(prev =>
          prev.map(d => (d.deck_id === updated.deck_id ? updated : d))
        )
        setDeckToRename(null)
      } else {
        // No body — treat like the add flow does: a 500-style failure.
        setRenameError(mapErrorToJapanese({ response: { status: 500 } }))
      }
    } catch (err: any) {
      setRenameError(mapErrorToJapanese(err))
    } finally {
      setRenaming(false)
    }
  }

  if (loading) {
    return (
      <section className="decks-section deck-management">
        <p className="deck-mgmt-loading">デッキを読み込み中...</p>
      </section>
    )
  }

  return (
    <section className="decks-section deck-management">
      <div className="deck-management-header">
        <h2>あなたのデッキ</h2>
        <button
          type="button"
          className="deck-mgmt-add-btn"
          onClick={() => {
            setValidationError(null)
            setShowAddForm(open => !open)
          }}
        >
          {showAddForm ? 'キャンセル' : 'デッキを追加'}
        </button>
      </div>

      {showAddForm && (
        <form className="deck-mgmt-form" onSubmit={handleAddSubmit}>
          <div className="deck-mgmt-field">
            <label htmlFor="deck-name">名前</label>
            <input
              id="deck-name"
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              maxLength={NAME_MAX}
              placeholder="デッキ名"
            />
          </div>
          <div className="deck-mgmt-field">
            <label htmlFor="deck-description">説明</label>
            <input
              id="deck-description"
              type="text"
              value={description}
              onChange={e => setDescription(e.target.value)}
              maxLength={DESCRIPTION_MAX}
              placeholder="任意"
            />
          </div>
          <div className="deck-mgmt-field">
            <label htmlFor="deck-category">カテゴリ</label>
            <input
              id="deck-category"
              type="text"
              value={category}
              onChange={e => setCategory(e.target.value)}
              maxLength={CATEGORY_MAX}
              placeholder="任意"
            />
          </div>
          {validationError && <div className="deck-mgmt-error">{validationError}</div>}
          <div className="deck-mgmt-form-actions">
            <button type="submit" className="deck-mgmt-submit-btn" disabled={creating}>
              {creating ? '追加中...' : '追加'}
            </button>
            <button type="button" onClick={resetForm} disabled={creating}>
              キャンセル
            </button>
          </div>
        </form>
      )}

      {error && <div className="deck-mgmt-error">{error}</div>}

      {decks.length === 0 ? (
        <p className="deck-mgmt-empty">デッキがありません。</p>
      ) : (
        <div className="deck-list">
          {decks.map(deck => (
            <div
              key={deck.deck_id}
              className="deck-card"
              role="button"
              tabIndex={0}
              onClick={() => onSelectDeck?.(deck.deck_id)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  onSelectDeck?.(deck.deck_id)
                }
              }}
            >
              <div className="deck-card-info">
                <h3>{deck.name}</h3>
                {deck.description && <p>{deck.description}</p>}
                <span className="deck-info">{deck.card_count || 0} 枚</span>
              </div>
              <div className="deck-card-actions">
                <button
                  type="button"
                  className="deck-mgmt-rename-btn"
                  aria-label={`デッキ「${deck.name}」の名前を変更`}
                  onClick={e => {
                    // Same isolation as the delete button: opening the
                    // rename modal must not also open the deck.
                    e.stopPropagation()
                    setDeckToRename(deck)
                    setRenameName(deck.name)
                    setRenameError(null)
                    setRenaming(false)
                  }}
                >
                  名前を変更
                </button>
                <button
                  type="button"
                  className="deck-mgmt-delete-btn"
                  aria-label={`デッキ「${deck.name}」を削除`}
                  onClick={e => {
                    // The whole card opens the deck; stop the click from
                    // bubbling up so deleting only opens the confirm modal.
                    e.stopPropagation()
                    setDeckToDelete(deck)
                    setDeleteError(null)
                  }}
                >
                  削除
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {deckToDelete && (
        <div
          className="modal-overlay"
          onClick={() => {
            if (!deleting) setDeckToDelete(null)
          }}
        >
          <div className="modal-content deck-mgmt-confirm" onClick={e => e.stopPropagation()}>
            <p className="deck-mgmt-confirm-text">
              「{deckToDelete.name}」を削除しますか？このデッキのカードは語彙に残ります。
            </p>
            {deleteError && <div className="deck-mgmt-error">{deleteError}</div>}
            <div className="deck-mgmt-confirm-actions">
              <button
                type="button"
                className="deck-mgmt-cancel-btn"
                onClick={() => setDeckToDelete(null)}
                disabled={deleting}
              >
                キャンセル
              </button>
              <button
                type="button"
                className="deck-mgmt-delete-confirm-btn"
                onClick={confirmDelete}
                disabled={deleting}
              >
                {deleting ? '削除中...' : '削除'}
              </button>
            </div>
          </div>
        </div>
      )}

      {deckToRename && (
        <div
          className="modal-overlay"
          onClick={() => {
            if (!renaming) setDeckToRename(null)
          }}
        >
          <div
            className="modal-content deck-mgmt-rename"
            onClick={e => e.stopPropagation()}
          >
            <form
              className="deck-mgmt-rename-form"
              onSubmit={handleRenameSubmit}
            >
              <h3>デッキ名を変更</h3>
              <div className="deck-mgmt-field">
                <label htmlFor="deck-rename-name">デッキ名</label>
                <input
                  id="deck-rename-name"
                  type="text"
                  value={renameName}
                  onChange={e => setRenameName(e.target.value)}
                  maxLength={NAME_MAX}
                  placeholder="デッキ名"
                />
              </div>
              {renameError && (
                <div className="deck-mgmt-error">{renameError}</div>
              )}
              <div className="deck-mgmt-confirm-actions">
                <button
                  type="button"
                  className="deck-mgmt-cancel-btn"
                  onClick={() => setDeckToRename(null)}
                  disabled={renaming}
                >
                  キャンセル
                </button>
                <button
                  type="submit"
                  className="deck-mgmt-rename-save-btn"
                  disabled={renaming}
                >
                  {renaming ? '保存中...' : '保存'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  )
}

export default DeckManagement
